import { Link } from 'react-router-dom'
import { useState, useEffect } from 'react'
import { 
  Target, Upload, BarChart3, Users, Settings, Play, 
  TrendingUp, Clock, CheckCircle, AlertCircle, Brain, Zap
} from 'lucide-react'
import { useAuth } from '../context/AuthContext'
import ProfessionalHeader from '../components/Layout/ProfessionalHeader'
import AnimatedCard from '../components/Common/AnimatedCard'
import GlowingButton from '../components/Common/GlowingButton'
import ParticlesBackground from '../components/Common/ParticlesBackground'

export default function Dashboard() {
  const { user, company } = useAuth()

  return (
    <div className="min-h-screen relative">
      <ParticlesBackground />
      <ProfessionalHeader />
      
      <main className="relative z-10 pt-24 pb-12 px-4 md:px-8">
        <div className="max-w-7xl mx-auto">
          
          {/* Hero Section moderne */}
          <div className="text-center mb-16 relative">
            <div className="absolute inset-0 bg-gradient-to-r from-pink-500/10 via-purple-500/10 to-blue-500/10 blur-3xl -z-10 animate-pulse"></div>
            
            <div className="flex flex-col md:flex-row items-center justify-center mb-8 space-y-6 md:space-y-0 md:space-x-8">
              <div className="relative group">
                <div className="absolute inset-0 bg-gradient-to-r from-pink-500 to-blue-500 rounded-3xl blur-xl opacity-50 group-hover:opacity-75 transition-opacity animate-pulse"></div>
                <div className="relative w-24 h-24 bg-gradient-to-r from-pink-500 to-blue-500 rounded-3xl flex items-center justify-center shadow-2xl">
                  <Brain className="w-12 h-12 text-white animate-pulse" />
                </div>
              </div>
              
              <div className="text-center md:text-left space-y-2">
                <h1 className="text-4xl md:text-6xl font-bold bg-gradient-to-r from-pink-400 via-purple-400 to-blue-400 bg-clip-text text-transparent leading-tight">
                  Resume Screening Pro
                </h1>
                <div className="flex items-center justify-center md:justify-start space-x-2">
                  <Zap className="w-5 h-5 text-yellow-400 animate-pulse" />
                  <p className="text-xl text-gray-300">Intelligence Artificielle Expert RH</p>
                </div>
                <p className="text-lg text-gray-400 max-w-md">
                  Bienvenue, <span className="text-blue-400 font-semibold">{user?.full_name || user?.email?.split('@')[0]}</span> 
                  {company?.name && <span className="text-gray-500"> • {company.name}</span>}
                </p>
              </div>
            </div>

            {/* Stats rapides en ligne */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-2xl mx-auto">
              <div className="glass-card p-4 text-center group hover:scale-105 transition-transform">
                <div className="text-2xl font-bold text-blue-400 mb-1">0</div>
                <div className="text-xs text-gray-400">CV Analysés</div>
              </div>
              <div className="glass-card p-4 text-center group hover:scale-105 transition-transform">
                <div className="text-2xl font-bold text-green-400 mb-1">0</div>
                <div className="text-xs text-gray-400">Candidats</div>
              </div>
              <div className="glass-card p-4 text-center group hover:scale-105 transition-transform">
                <div className="text-2xl font-bold text-purple-400 mb-1">0</div>
                <div className="text-xs text-gray-400">Campagnes</div>
              </div>
              <div className="glass-card p-4 text-center group hover:scale-105 transition-transform">
                <div className="text-2xl font-bold text-orange-400 mb-1">-</div>
                <div className="text-xs text-gray-400">Taux Succès</div>
              </div>
            </div>
          </div>

          {/* Actions principales avec design IA moderne */}
          <div className="grid lg:grid-cols-2 gap-8 mb-16">
            
            {/* Nouveau screening avec effet cyber */}
            <AnimatedCard className="p-0 overflow-hidden group hover:scale-[1.02] transition-all duration-500" delay="0.3s">
              <div className="relative h-full bg-gradient-to-br from-pink-500/10 via-purple-500/10 to-blue-500/10">
                <div className="absolute inset-0 bg-gradient-to-br from-pink-500/20 via-purple-500/20 to-blue-500/20 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                
                {/* Effet de grille cyber */}
                <div className="absolute inset-0 opacity-20">
                  <div className="absolute inset-0" style={{
                    backgroundImage: `linear-gradient(rgba(69, 202, 255, 0.1) 1px, transparent 1px),
                                    linear-gradient(90deg, rgba(69, 202, 255, 0.1) 1px, transparent 1px)`,
                    backgroundSize: '20px 20px'
                  }}></div>
                </div>
                
                <div className="relative p-8 h-full flex flex-col">
                  <div className="text-center flex-1 space-y-6">
                    <div className="relative inline-block">
                      <div className="absolute inset-0 bg-gradient-to-r from-pink-500 to-blue-500 rounded-3xl blur-lg opacity-50 group-hover:opacity-75 transition-opacity animate-pulse"></div>
                      <div className="relative w-20 h-20 bg-gradient-to-r from-pink-500 to-blue-500 rounded-3xl flex items-center justify-center shadow-2xl group-hover:scale-110 transition-transform">
                        <Play className="w-10 h-10 text-white" />
                      </div>
                    </div>
                    
                    <h2 className="text-3xl font-bold text-white mb-4 group-hover:text-transparent group-hover:bg-gradient-to-r group-hover:from-pink-400 group-hover:to-blue-400 group-hover:bg-clip-text transition-all">
                      Nouveau Screening IA
                    </h2>
                    
                    <div className="space-y-3">
                      <p className="text-gray-300 leading-relaxed">
                        Analysez vos CV avec <span className="text-blue-400 font-semibold">notre IA Expert</span> en quelques minutes
                      </p>
                      <div className="flex items-center justify-center space-x-4 text-sm text-gray-400">
                        <div className="flex items-center space-x-1">
                          <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
                          <span>Jusqu'à 1000 CV</span>
                        </div>
                        <div className="flex items-center space-x-1">
                          <div className="w-2 h-2 bg-blue-400 rounded-full animate-pulse"></div>
                          <span>Analyse IA avancée</span>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="text-center">
                    <Link to="/screening">
                      <GlowingButton size="lg" className="group/btn">
                        <Upload className="w-6 h-6 mr-3 group-hover/btn:scale-110 transition-transform" />
                        Commencer l'Analyse
                        <div className="absolute inset-0 bg-gradient-to-r from-pink-500/20 to-blue-500/20 rounded-xl opacity-0 group-hover/btn:opacity-100 transition-opacity"></div>
                      </GlowingButton>
                    </Link>
                  </div>
                </div>
              </div>
            </AnimatedCard>

            {/* Configuration avec design tech */}
            <AnimatedCard className="p-0 overflow-hidden group hover:scale-[1.02] transition-all duration-500" delay="0.4s">
              <div className="relative h-full bg-gradient-to-br from-gray-800/20 via-gray-700/20 to-gray-600/20">
                <div className="absolute inset-0 bg-gradient-to-br from-gray-600/20 via-gray-500/20 to-gray-400/20 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                
                <div className="relative p-8 h-full flex flex-col">
                  <div className="text-center flex-1 space-y-6">
                    <div className="relative inline-block">
                      <div className="absolute inset-0 bg-gradient-to-r from-gray-500 to-gray-700 rounded-3xl blur-lg opacity-30 group-hover:opacity-50 transition-opacity"></div>
                      <div className="relative w-20 h-20 bg-gradient-to-r from-gray-600 to-gray-800 rounded-3xl flex items-center justify-center shadow-2xl group-hover:scale-110 transition-transform">
                        <Settings className="w-10 h-10 text-white" />
                      </div>
                    </div>
                    
                    <h2 className="text-3xl font-bold text-white mb-4 group-hover:text-gray-300 transition-colors">
                      Configuration
                    </h2>
                    
                    <div className="space-y-3">
                      <p className="text-gray-300 leading-relaxed">
                        Configurez vos critères de recrutement et paramètres IA
                      </p>
                      <div className="flex items-center justify-center space-x-4 text-sm text-gray-400">
                        <div className="flex items-center space-x-1">
                          <div className="w-2 h-2 bg-purple-400 rounded-full animate-pulse"></div>
                          <span>Critères avancés</span>
                        </div>
                        <div className="flex items-center space-x-1">
                          <div className="w-2 h-2 bg-yellow-400 rounded-full animate-pulse"></div>
                          <span>Profil entreprise</span>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="text-center">
                    <Link to="/settings">
                      <GlowingButton variant="secondary" size="lg">
                        <Settings className="w-6 h-6 mr-3" />
                        Paramètres
                      </GlowingButton>
                    </Link>
                  </div>
                </div>
              </div>
            </AnimatedCard>
          </div>

          {/* Section info moderne */}
          <div className="grid md:grid-cols-3 gap-8">
            
            {/* Fonctionnalités IA */}
            <AnimatedCard delay="0.5s" className="text-center group hover:scale-105 transition-all duration-300">
              <div className="relative">
                <div className="w-16 h-16 bg-gradient-to-r from-blue-500 to-purple-500 rounded-2xl flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform">
                  <Brain className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-xl font-bold text-white mb-3">IA Expert RH</h3>
                <p className="text-gray-400 text-sm leading-relaxed">
                  Modèle d'IA de dernière génération pour une analyse précise et contextuelle des profils candidats
                </p>
              </div>
            </AnimatedCard>

            {/* Vitesse de traitement */}
            <AnimatedCard delay="0.6s" className="text-center group hover:scale-105 transition-all duration-300">
              <div className="relative">
                <div className="w-16 h-16 bg-gradient-to-r from-green-500 to-emerald-500 rounded-2xl flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform">
                  <Zap className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-xl font-bold text-white mb-3">Traitement Ultra-Rapide</h3>
                <p className="text-gray-400 text-sm leading-relaxed">
                  Analyse complète en 30-60 secondes par CV avec traitement parallélisé pour les gros volumes
                </p>
              </div>
            </AnimatedCard>

            {/* Sécurité */}
            <AnimatedCard delay="0.7s" className="text-center group hover:scale-105 transition-all duration-300">
              <div className="relative">
                <div className="w-16 h-16 bg-gradient-to-r from-pink-500 to-red-500 rounded-2xl flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform">
                  <CheckCircle className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-xl font-bold text-white mb-3">Sécurité & Conformité</h3>
                <p className="text-gray-400 text-sm leading-relaxed">
                  Conformité RGPD, chiffrement AES-256 et suppression automatique des données sensibles
                </p>
              </div>
            </AnimatedCard>
          </div>
        </div>
      </main>
    </div>
  )
}